import {
  Component,
  signal,
  computed,
  input,
  output,
  inject,
  ChangeDetectionStrategy,
  OnInit,
  ViewChild,
  ElementRef,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { OrganizationService } from '../../../../core/services/organization.service';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { BaseStripeComponentClass } from '../../../../core/base-classes/base-stripe-component-class';
import { StripeConnectionResponse } from '../../../../core/models/service/stripe-connect.model';
import { CommonChecks } from '../../../../core/utilities/common-checks.utilities';
import { ConfirmationModalComponent } from "../../../../parts/confirmation-modal/confirmation-modal.component";
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { InfoModalComponent } from "../../../../parts/info-modal/info-modal.component";
import { DisableWithoutPermissionDirective } from "../../../../core/directives/disable-without-permission/disable-without-permission.directive";
import { StripePermission } from '../../../../core/enums/permissions.enums';
import { StripeMessagingConstants } from '../../../../core/constants/messaging.constants';
import { OrganizationSettingModel, UpdateOrganizationSettingRequest } from '../../../../core/models/data/organization-setting.model';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { ProfileOrganizationScopeService } from '../../../../core/services/authorization/profile-organization-scope.service';
import { PermissionUtilities } from '../../../../core/utilities/permission.utilities';
import { PermissionDirectiveOptions } from '../../../../core/models/ui/permission-directive-options.model';

@Component({
  selector: 'ms-stripe-organization-onboarding',
  standalone: true,
  imports: [CommonModule, ConfirmationModalComponent, InfoModalComponent, DisableWithoutPermissionDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './stripe-organization-onboarding.component.html',
})
export class StripeOrganizationOnboardingComponent extends BaseStripeComponentClass implements OnInit {
  @ViewChild('onboardingContainer', { read: ElementRef }) onboardingContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('stripeConfirmationModal', { read: ConfirmationModalComponent }) stripeConfirmationModal?: ConfirmationModalComponent;
  @ViewChild('stripeInfoModal', { read: InfoModalComponent }) stripeInfoModal?: InfoModalComponent;

  private orgService = inject(OrganizationService);
  private authorizationService = inject(AuthorizationService);
  private authorizationStateService = inject(AuthorizationStateService);
  private userSessionService = inject(UserSessionService);
  private profileScope = inject(ProfileOrganizationScopeService, { optional: true });

  // Shared by the directive on each onboarding control and by canCreateStripeAccount below, so the
  // greyed-out button and the help text explaining it can never disagree (DEV-2704).
  public readonly createAccountPermission: PermissionDirectiveOptions = {
    requiredPermissions: [StripePermission.CreateAccount],
    userPermissionOnly: false
  };

  public readonly unlinkPermission: PermissionDirectiveOptions = {
    requiredPermissions: [StripePermission.Unlink],
    userPermissionOnly: false
  };

  public readonly ownerOnlyHelpTextId = 'stripe-onboarding-owner-only-help';
  public readonly ownerOnlyHelpText = StripeMessagingConstants.STRIPE_ONBOARDING_OWNER_ONLY();
  public readonly disconnectOwnerOnlyHelpTextId = 'stripe-disconnect-owner-only-help';
  public readonly disconnectOwnerOnlyHelpText = StripeMessagingConstants.STRIPE_DISCONNECT_OWNER_ONLY();

  public readonly canCreateStripeAccount = computed(() => this.hasPermission(this.createAccountPermission));
  public readonly canUnlinkStripe = computed(() => this.hasPermission(this.unlinkPermission));

  public organization = input.required<OrganizationModel>();
  public organizationSettings = input.required<OrganizationSettingModel>();
  public onboardingStarted = output<void>();
  public onboardingComplete = output<string>();
  public onboardingExit = output<void>();

  public showEmbeddedOnboarding = signal(false);
  public stripeConnection = signal<StripeConnectionResponse | null>(null);
  public isLoadingConnection = signal(false);

  public canStartOnboarding = computed(() => {
    const connection = this.stripeConnection();
    return !connection?.isOnboardingComplete;
  });

  public isOnboardingComplete = computed(() => {
    const connection = this.stripeConnection();
    return connection?.isOnboardingComplete === true;
  });

  public isPending = computed(() => {
    const connection = this.stripeConnection();
    return !CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(connection?.stripeAccountId) && !connection?.isOnboardingComplete;
  });

  // The GL account Stripe payments post to. System-assigned at organization creation, so it is
  // shown here purely so the user knows where the cash lands — there is nothing to choose or save.
  public stripeBalanceAccount = computed(() => this.stripeConnection()?.stripeBalanceAccount);

  constructor(private bootstrapService: BootstrapService) {
    super();

    effect(() => {
      const connection = this.stripeConnection();
      if (connection?.isOnboardingComplete && this.showEmbeddedOnboarding()) {
        this.cleanupStripeComponent();
        this.showEmbeddedOnboarding.set(false);
      }
    });
  }

  async ngOnInit(): Promise<void> {
    await this.checkConnectionStatus();
  }

  private hasPermission(options: PermissionDirectiveOptions): boolean {
    // Answers "has permission" while the authorization state is still loading, matching
    // disableWithoutPermission, so the help text never flashes beneath a still-enabled button.
    if (!this.authorizationStateService.authorizationState()) {
      return true;
    }

    return PermissionUtilities.shouldOptionsRender(
      options,
      this.authorizationService,
      this.authorizationStateService,
      this.userSessionService,
      this.profileScope
    );
  }

  private async checkConnectionStatus(): Promise<void> {
    this.isLoadingConnection.set(true);
    this.clearError();

    try {
      const result = await this.orgService.getStripeConnection(
        this.organization().organizationId
      );

      if (result.success && result.data) {
        this.stripeConnection.set(result.data);
      } else {
        console.error('Failed to get Stripe connection status:', result.message);
      }
    } catch (error: any) {
      console.error('Error checking Stripe connection:', error);
      this.handleStripeError(error);
    } finally {
      this.isLoadingConnection.set(false);
    }
  }

  public async startOnboarding(): Promise<void> {
    await this.initializeEmbeddedOnboarding();
  }

  private async initializeEmbeddedOnboarding(): Promise<void> {
    this.isInitializing.set(true);
    this.clearError();
    this.onboardingStarted.emit();

    try {
      const accountSessionResult = await this.orgService.getStripeAccountSession(
        this.organization().organizationId
      );

      if (!accountSessionResult.success || !accountSessionResult.data?.clientSecret) {
        throw new Error(accountSessionResult.message || StripeMessagingConstants.STRIPE_ERROR_FAILED_TO_GET_ACCOUNT_SESSION());
      }

      await this.initializeStripeConnect(accountSessionResult.data.clientSecret);

      this.showEmbeddedOnboarding.set(true);

      setTimeout(() => {
        this.mountEmbeddedOnboarding();
      }, 0);

    } catch (error: any) {
      this.handleStripeError(error);
      this.showEmbeddedOnboarding.set(false);
    } finally {
      this.isInitializing.set(false);
    }
  }

  private async mountEmbeddedOnboarding(): Promise<void> {
    if (!this.stripeConnectInstance || !this.onboardingContainer) {
      return;
    }

    try {
      this.createStripeComponent('account-onboarding');

      if (typeof this.stripeComponent.setOnExit === 'function') {
        this.stripeComponent.setOnExit(async () => {
          await this.handleOnboardingExit();
        });
      }

      this.setCollectionOptions({
        fields: 'eventually_due',
        futureRequirements: 'include'
      });

      this.appendStripeComponent(this.onboardingContainer.nativeElement);

    } catch (error) {
      this.handleStripeError(error);
      this.showEmbeddedOnboarding.set(false);
    }
  }

  private async handleOnboardingExit(): Promise<void> {
    try {
      const orgSettings = this.organizationSettings();
      const updateRequest: UpdateOrganizationSettingRequest = {
        replyToEmailAddress: orgSettings.replyToEmailAddress,
        currency: orgSettings.currency.currencyId,
        timeZoneId: orgSettings.timeZoneId,
        isStripeOnboardingComplete: true,
        // Carried through unchanged: this PATCH replaces the settings record, so leaving it out would
        // reset the organization's member directory default as a side effect of finishing onboarding.
        includeMembersInDirectoryByDefault: orgSettings.includeMembersInDirectoryByDefault
      };

      const updateResult = await this.orgService.updateOrganizationSettings(
        orgSettings.organizationId,
        updateRequest
      );

      if (updateResult.success) {
        const currentConnection = this.stripeConnection();
        if (currentConnection) {
          this.stripeConnection.set({
            ...currentConnection,
            isOnboardingComplete: true
          });
        }

        this.onboardingComplete.emit(currentConnection?.stripeAccountId || '');
      } else {
        console.error('Failed to update organization status:', updateResult.message);
      }
    } catch (error: any) {
      console.error('Error updating organization onboarding status:', error);
    } finally {
      this.onboardingExit.emit();
      this.cleanupStripeComponent();
      this.showEmbeddedOnboarding.set(false);
    }
  }

  public cancelOnboarding(): void {
    this.cleanupStripeComponent();
    this.showEmbeddedOnboarding.set(false);
  }

  public async disconnectStripe(): Promise<void> {
    this.isLoadingConnection.set(true);
    this.clearError();
    try {
      const result = await this.orgService.deleteStripeConnection(
        this.organization().organizationId
      );
      if (result.success) {
        this.stripeConnection.set(null);
        // The API clears IsStripeOnboardingComplete during the unlink, so the cached settings have
        // to be refetched or every surface gated on that flag keeps the pre-disconnect answer.
        void this.userSessionService.refreshOrganizationSettings();
        this.stripeInfoModal?.message.set(StripeMessagingConstants.STRIPE_ORGANIZATION_DISCONNECTED());
        this.stripeInfoModal?.modalType.set(ModalTypes.Success);
      } else {
        this.stripeInfoModal?.message.set(result.message || StripeMessagingConstants.STRIPE_ERROR_ORGANIZATION_DISCONNECTED());
        this.stripeInfoModal?.modalType.set(ModalTypes.Failure);
        console.error(StripeMessagingConstants.STRIPE_ERROR_ORGANIZATION_DISCONNECTED(), result.message);
      }
      this.bootstrapService.manageModal('#stripeConfirmationModal', ModalActions.Hide);
      this.bootstrapService.manageModal('#stripeInfoModal', ModalActions.Show);
    } catch (error: any) {
      console.error(StripeMessagingConstants.STRIPE_ERROR_ORGANIZATION_DISCONNECTED(), error);
      this.handleStripeError(error);
    } finally {
      this.isLoadingConnection.set(false);
    }
  }

  public openDisconnectConfirmationModal(): void {
    this.bootstrapService.manageModal('#stripeConfirmationModal', ModalActions.Show);
  }

  public cancelDisconnectStripe(): void {
    this.bootstrapService.manageModal('#stripeConfirmationModal', ModalActions.Hide);
  }

  public closeInfoModal(): void {
    this.bootstrapService.manageModal('#stripeInfoModal', ModalActions.Hide);
  }

  override ngOnDestroy(): void {
    this.cleanupStripeComponent();
  }
}
